import React from 'react';
import { mbTextStyles } from '@/utils/general/style-utils';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { COLORS } from '../../../constants/colors';
import { textStyles } from '../../../constants/textStyles';
import { useGetContentInventory } from '../../../hooks/adminHooks';
import { ComponentWrapper } from '../../helperComponents/componentWrapper/ComponentWrapper';
import { ContentInventoryCategory, ContentInventoryGameType } from '../../../typesAndInterfaces/typesAndInterfaces';

// "Games" is the user-facing name for the IntroGame tab; surface labels here too.
const GAME_TYPE_LABELS: Record<string, string> = {
  IntroGame: 'Games (Intro)',
  MatchingItemsGame: 'Matching Items',
  BodyParts: 'Body Parts',
  Geography: 'Geography',
  Extras: 'Lessons (Extras)',
};

const CategoryRow = ({ category }: { category: ContentInventoryCategory }) => (
  <View style={styles.categoryRow}>
    <Text style={[styles.categoryName, !category.isEnabled && styles.disabledText]} numberOfLines={1}>
      {category.name}{!category.isEnabled ? '  (disabled)' : ''}
    </Text>
    <Text style={styles.categoryCount}>{category.numberOfQuestions} items</Text>
  </View>
);

const GameTypeSection = ({ section }: { section: ContentInventoryGameType }) => (
  <View style={styles.section}>
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionTitle}>{GAME_TYPE_LABELS[section.gameType] ?? section.gameType}</Text>
      <Text style={styles.sectionSummary}>{section.categoryCount} categories · {section.totalItems} items</Text>
    </View>
    {section.categories.length === 0
      ? <Text style={styles.emptyText}>No categories</Text>
      : section.categories.map(category => <CategoryRow key={category._id} category={category} />)
    }
  </View>
);

const ContentInventory = () => {
  const { data: inventory, isLoading } = useGetContentInventory();

  return (
    <ComponentWrapper
      containerStyle={styles.container}
      disableLinearGradient
      wrapInScrollView
      hideWebHeader
    >
      <Text style={styles.pageTitle}>Content Report</Text>
      <Text style={styles.pageSubtitle}>What is in each game type (categories and item counts).</Text>

      {isLoading
        ? <ActivityIndicator color={COLORS.white} style={{ marginTop: 40 }} />
        : (inventory ?? []).map(section => <GameTypeSection key={section.gameType} section={section} />)
      }
    </ComponentWrapper>
  );
};

export { ContentInventory };

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  pageTitle: mbTextStyles([textStyles.largeText, { fontWeight: 'bold', textAlign: 'left' }]),
  pageSubtitle: mbTextStyles([textStyles.smallText, { textAlign: 'left', color: COLORS.lighterGray, marginTop: 6, marginBottom: 20 }]),
  section: {
    backgroundColor: COLORS.bottomBarActiveBackground,
    borderRadius: 10,
    padding: 16,
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    paddingBottom: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: COLORS.lighterGray,
  },
  sectionTitle: mbTextStyles([textStyles.normalText, { fontWeight: 'bold', textAlign: 'left' }]),
  sectionSummary: mbTextStyles([textStyles.smallText, { color: COLORS.lighterGray }]),
  categoryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
  },
  categoryName: mbTextStyles([textStyles.smallText, { textAlign: 'left', flex: 1, marginRight: 12 }]),
  categoryCount: mbTextStyles([textStyles.smallText, { color: COLORS.lighterGray }]),
  disabledText: { color: COLORS.lighterGray },
  emptyText: mbTextStyles([textStyles.smallText, { color: COLORS.lighterGray, textAlign: 'left' }]),
});
